import React, { useContext } from 'react'

import {
  useRenameProjectMutation,
  GetProjectsDocument,
  GetProjectsQueryVariables,
  ProjectFragment,
} from '@sketch/gql-types'

import { ToastContext } from '@sketch/toasts'

import { InlineEditorForm } from './InlineEditor.styles'
import { privacyIcon } from '../ProjectListItem/ProjectListItem.utils'
import { useFolderTerm } from '@sketch/modules-common'

const typeInline = <T extends any>(variables: T) => variables

interface RenameInlineEditorProps {
  project: ProjectFragment
  workspaceId: string
  placeholder: string
  toggleEditor: () => void
  onCompleted?: () => void
  depth?: number
}

interface FormValues {
  projectName: string
}

const RenameInlineEditor = ({
  project,
  workspaceId,
  placeholder,
  toggleEditor,
  onCompleted,
  depth = 0,
}: RenameInlineEditorProps) => {
  const { showToast } = useContext(ToastContext)
  const folderTerm = useFolderTerm()

  const [renameProject] = useRenameProjectMutation({
    redirectErrors: true,
    onCompleted: () => {
      showToast(`${folderTerm.upper} was renamed successfully`)
      toggleEditor()
      onCompleted?.()
    },
    onError: error => {
      toggleEditor()
      showToast(error.message, 'negative')
    },
    refetchQueries: [
      {
        query: GetProjectsDocument,
        variables: typeInline<GetProjectsQueryVariables>({
          workspaceId,
        }),
      },
    ],
  })

  const onSubmit = ({ projectName: newProjectName }: FormValues) => {
    if (project.name === newProjectName) {
      toggleEditor()
      return
    }

    return renameProject({
      variables: {
        name: newProjectName,
        projectId: project.identifier,
      },
    })
  }

  const icon = privacyIcon(project)

  return (
    <InlineEditorForm
      depth={depth}
      dataTestId="rename-folder-form"
      placeholder={placeholder}
      onSubmit={onSubmit}
      toggleEditor={toggleEditor}
      defaultValue={project.name}
      icon={icon}
    />
  )
}

export { RenameInlineEditor }
